import { Box, IconButton, Typography } from '@mui/material'
import { Copy01Icon } from '@untitled-theme/icons-react'
import { toast } from 'react-toastify'

interface BAFAMetricsDisplayProps {
  usableSpaceInSqm: number
  currentTransmissionHeatLossInWPerK?: number
  retrofitTransmissionHeatLossInWPerK?: number
  currentFinalEnergyInKwh: number
  retrofitFinalEnergyInKwh: number
  currentPrimaryEnergyInKwh?: number
  retrofitPrimaryEnergyInKwh?: number
  currentCo2EmissionsInTons?: number
  retrofitCo2EmissionsInTons?: number
}

const formatNumber = (value: number): string =>
  value.toLocaleString('de-DE', { maximumFractionDigits: 1 })

const copyToClipboard = (value: number) => {
  navigator.clipboard.writeText(String(Math.round(value))).then(() => {
    toast.success('Wert kopiert', { autoClose: 1500 })
  })
}

const MetricRow = ({
  label,
  value,
  unit,
  rawValue,
}: {
  label: string
  value: string
  unit: string
  rawValue: number
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
    }}
  >
    <Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#464A54' }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#181D29' }}>
        {value} {unit}
      </Typography>
      <IconButton
        size="small"
        onClick={() => copyToClipboard(rawValue)}
        aria-label={`${label} kopieren`}
        sx={{ padding: '2px' }}
      >
        <Copy01Icon style={{ fontSize: '16px' }} />
      </IconButton>
    </Box>
  </Box>
)

const MetricSection = ({
  title,
  children,
}: {
  title: React.ReactNode
  children: React.ReactNode
}) => (
  <Box
    sx={{
      border: '1px solid #E1E1E4',
      borderRadius: '16px',
      padding: '16px 24px',
    }}
  >
    <Typography component="h2" sx={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px' }}>
      {title}
    </Typography>
    {children}
  </Box>
)

const BAFAMetricsDisplay = ({
  usableSpaceInSqm,
  currentTransmissionHeatLossInWPerK,
  retrofitTransmissionHeatLossInWPerK,
  currentFinalEnergyInKwh,
  retrofitFinalEnergyInKwh,
  currentPrimaryEnergyInKwh,
  retrofitPrimaryEnergyInKwh,
  currentCo2EmissionsInTons,
  retrofitCo2EmissionsInTons,
}: BAFAMetricsDisplayProps) => {
  const showTransmissionHeatLoss =
    currentTransmissionHeatLossInWPerK != null || retrofitTransmissionHeatLossInWPerK != null
  const showPrimaryEnergy = currentPrimaryEnergyInKwh != null || retrofitPrimaryEnergyInKwh != null
  const showCo2Emissions = currentCo2EmissionsInTons != null || retrofitCo2EmissionsInTons != null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <MetricSection
        title={
          <>
            Gebäudenutzfläche A<sub>N</sub>
          </>
        }
      >
        <MetricRow
          label="Im Ausgangszustand"
          value={formatNumber(usableSpaceInSqm)}
          unit="m²"
          rawValue={usableSpaceInSqm}
        />
        <MetricRow
          label="Nach Sanierung"
          value={formatNumber(usableSpaceInSqm)}
          unit="m²"
          rawValue={usableSpaceInSqm}
        />
      </MetricSection>

      {showTransmissionHeatLoss && (
        <MetricSection
          title={
            <>
              Transmissionswärmeverlust H<sub>T</sub>
            </>
          }
        >
          {currentTransmissionHeatLossInWPerK != null && (
            <MetricRow
              label="Im Ausgangszustand"
              value={formatNumber(currentTransmissionHeatLossInWPerK)}
              unit="W/K"
              rawValue={currentTransmissionHeatLossInWPerK}
            />
          )}
          {retrofitTransmissionHeatLossInWPerK != null && (
            <MetricRow
              label="Nach Sanierung"
              value={formatNumber(retrofitTransmissionHeatLossInWPerK)}
              unit="W/K"
              rawValue={retrofitTransmissionHeatLossInWPerK}
            />
          )}
        </MetricSection>
      )}

      <MetricSection
        title={
          <>
            Endenergiebedarf Q<sub>E</sub>
          </>
        }
      >
        <MetricRow
          label="Im Ausgangszustand"
          value={formatNumber(currentFinalEnergyInKwh)}
          unit="kWh/a"
          rawValue={currentFinalEnergyInKwh}
        />
        <MetricRow
          label="Nach Sanierung"
          value={formatNumber(retrofitFinalEnergyInKwh)}
          unit="kWh/a"
          rawValue={retrofitFinalEnergyInKwh}
        />
      </MetricSection>

      {showPrimaryEnergy && (
        <MetricSection
          title={
            <>
              Primärenergiebedarf Q<sub>P</sub>
            </>
          }
        >
          {currentPrimaryEnergyInKwh != null && (
            <MetricRow
              label="Im Ausgangszustand"
              value={formatNumber(currentPrimaryEnergyInKwh)}
              unit="kWh/a"
              rawValue={currentPrimaryEnergyInKwh}
            />
          )}
          {retrofitPrimaryEnergyInKwh != null && (
            <MetricRow
              label="Nach Sanierung"
              value={formatNumber(retrofitPrimaryEnergyInKwh)}
              unit="kWh/a"
              rawValue={retrofitPrimaryEnergyInKwh}
            />
          )}
        </MetricSection>
      )}

      {showCo2Emissions && (
        <MetricSection title={<>Äquivalente CO₂-Emissionen</>}>
          {currentCo2EmissionsInTons != null && (
            <MetricRow
              label="Im Ausgangszustand"
              value={formatNumber(currentCo2EmissionsInTons)}
              unit="t/a"
              rawValue={currentCo2EmissionsInTons}
            />
          )}
          {retrofitCo2EmissionsInTons != null && (
            <MetricRow
              label="Nach Sanierung"
              value={formatNumber(retrofitCo2EmissionsInTons)}
              unit="t/a"
              rawValue={retrofitCo2EmissionsInTons}
            />
          )}
        </MetricSection>
      )}
    </Box>
  )
}

export default BAFAMetricsDisplay
